import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaceTaskScreen } from '@symfa-inc/providence-verizon-types';
import { Form } from 'antd';
import classNames from 'classnames';
import { HttpService } from '@core/services';
import { ProjectHttpService } from '@core/services/http';
import {
  useDidUpdateEffect,
  usePMTabPrefetch,
  useSaveChanged,
} from '@core/utils/hooks';
import {
  getProjectBundleIDParamFromUrl,
  isValidForm,
} from '@core/utils/methods';
import { ALLOW_OOM_INFORMATION_PACE_TASK_OPTIONS } from '@models/constants';
import {
  FinancialOOMInformation,
  OptionProps,
  ProjectPaceTaskData,
} from '@models/interfaces';
import { SummaryProps } from '@models/types';
import {
  getPaceTaskDifference,
  NotificationsLoader,
  paceTaskMapper,
  PaceTaskTable,
  Panel,
} from '@shared/components';
import { PrimaryButton, ShowMoreInput } from '@shared/modules';
import {
  CommonActions,
  FinancialActions,
  ProjectActions,
} from '@store/actions';
import {
  FinancialSelectors,
  PaceTasksSelectors,
  ProjectFuzeIDsSelectors,
  ProjectManagementSelectors,
  UserSelectors,
} from '@store/selectors';
import { ProjectInformation } from '../project-information';
import { SiteInformation } from '../site-information';

import './styles.scss';

const { useForm } = Form;

export const ProjectManagementSummary: FC<SummaryProps> = ({
  isEditing = false,
  toggleEditing,
}: PropsWithChildren<SummaryProps>) => {
  const dispatch = useDispatch();

  const [form] = useForm();
  const [paceTaskForm] = useForm();

  const { engineering, id, leasing, projectNote, site, ...project } =
    useSelector(ProjectManagementSelectors.getPMSummaryInfo);

  const currentPaceTasks = useSelector(PaceTasksSelectors.getPaceTasks);
  const projectFuzeIDsTableVisible = useSelector(
    ProjectFuzeIDsSelectors.getProjectFuzeIDsTableVisible,
  );
  const arePaceTasksChanged = useSelector(
    PaceTasksSelectors.arePaceTasksChanged,
  );
  const PACEFetching = useSelector(PaceTasksSelectors.isFetching);
  const financialOOMInformation = useSelector(
    FinancialSelectors.getOOMInformationData,
  );
  const roles = useSelector(UserSelectors.getUserRoles);

  const onSubmit = useCallback(async (): Promise<void> => {
    const [paceTasks, keys] = getPaceTaskDifference(
      currentPaceTasks,
      paceTaskForm,
      roles,
    );

    if (await isValidForm(paceTaskForm, keys)) {
      try {
        await HttpService.getHttpRequests(
          ProjectHttpService,
        ).updateProjectPaceTasks(
          id,
          PaceTaskScreen.ProjectManagement,
          paceTasks,
        );

        toggleEditing?.();

        dispatch(ProjectActions.setArePACETasksChanged.done(false));

        const OOMPaceTaskIds: string[] =
          ALLOW_OOM_INFORMATION_PACE_TASK_OPTIONS.map(
            ({ value }: OptionProps) => value as string,
          );

        (paceTasks as ProjectPaceTaskData[]).forEach(
          ({ paceTask: paceTaskId, actualDate }: ProjectPaceTaskData) => {
            if (paceTaskId && OOMPaceTaskIds.includes(paceTaskId)) {
              const updateOOMInformationRecord = financialOOMInformation.find(
                (oom: FinancialOOMInformation) =>
                  oom.PACETaskIdentification === paceTaskId,
              );

              if (updateOOMInformationRecord) {
                dispatch(
                  FinancialActions.updateOOMInformationDataAction.done({
                    ...updateOOMInformationRecord,
                    actualDate,
                  }),
                );
              }
            }
          },
        );

        NotificationsLoader.notificationSuccess(
          'PACE tasks have been updated!',
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [currentPaceTasks, paceTaskForm, roles, id, financialOOMInformation]);

  const onCancel = useCallback(async (): Promise<void> => {
    paceTaskForm.setFieldsValue(paceTaskMapper(currentPaceTasks));

    toggleEditing?.();

    dispatch(ProjectActions.setArePACETasksChanged.done(false));
  }, [paceTaskForm, currentPaceTasks]);

  const buttonsDisabled = useMemo(
    () => PACEFetching || !arePaceTasksChanged,
    [PACEFetching, arePaceTasksChanged],
  );

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },
    [],
  );

  useDidUpdateEffect(() => {
    dispatch(CommonActions.setHasUnsubmittedData.done(arePaceTasksChanged));
  }, [arePaceTasksChanged]);

  useSaveChanged(isEditing, onSubmit, onCancel);

  const [projectBundleID] = getProjectBundleIDParamFromUrl();

  usePMTabPrefetch('summary', projectBundleID);

  return (
    <div className="prov-project-management-summary">
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isEditing,
          'fuze-ids-table-open': projectFuzeIDsTableVisible,
        })}
      >
        <div className="prov-project-management-summary__wrap">
          <div className="info-panel">
            <SiteInformation data={site} className="info-panel__information" />
            <ProjectInformation
              data={{
                structuralNotes: leasing?.structural?.structuralComment,
                structuralResult: leasing?.structural?.structuralResult?.value,
                ...project,
              }}
              className="info-panel__information"
            />
            <Form
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
              form={form}
              className="info-panel__notes"
            >
              <Panel header="Notes">
                <ShowMoreInput
                  id="siteNotes"
                  label="Site Notes"
                  data={site?.siteNote}
                  form={form}
                  isEditing={false}
                />
                <ShowMoreInput
                  id="projectNotes"
                  label="Project Notes"
                  data={projectNote}
                  form={form}
                  isEditing={false}
                />
                <ShowMoreInput
                  id="engineeringNote"
                  label="Engineering Notes"
                  data={engineering?.RFDSPhase1?.engineeringNote}
                  form={form}
                  isEditing={false}
                />
                <ShowMoreInput
                  id="leasingNote"
                  label="Leasing Notes"
                  data={leasing?.leasingNote}
                  form={form}
                  isEditing={false}
                />
                <ShowMoreInput
                  id="projectPermittingNote"
                  label="Project Permitting Notes"
                  data={project?.projectPermittingNote}
                  form={form}
                  isEditing={false}
                />
              </Panel>
            </Form>
          </div>
          <PaceTaskTable
            isEditing={isEditing}
            data={currentPaceTasks || []}
            form={paceTaskForm}
          />
        </div>
      </div>
      {isEditing && (
        <div className="prov-project-management-summary__btns">
          <div className="action-wrap">
            <PrimaryButton
              title="Submit"
              disabled={buttonsDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton
              title="Cancel"
              disabled={buttonsDisabled}
              type="default"
              onClick={onCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};
